import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import {
  getPhysicalMetrics,
  createPhysicalMetric,
  getVaccinations,
  createVaccination,
  updateVaccination,
  getGroomingRecords,
  createGroomingRecord,
  updateGroomingRecord,
  getMedicalUpdates,
  createMedicalUpdate,
  updateMedicalUpdate,
  CreatePhysicalMetricRequest,
  CreateVaccinationRequest,
  UpdateVaccinationRequest,
  CreateGroomingRecordRequest,
  UpdateGroomingRecordRequest,
  CreateMedicalUpdateRequest,
  UpdateMedicalUpdateRequest,
  PhysicalMetric,
  Vaccination,
  GroomingRecord,
  MedicalUpdate,
} from '@/api/pets';

/**
 * Blockchain sync runs in the background on the server (via waitUntil).
 * Schedule two refetches to pick up the txSignature once confirmed.
 */
function scheduleBlockchainRefetch(queryClient: QueryClient, queryKey: readonly unknown[]) {
  setTimeout(() => queryClient.invalidateQueries({ queryKey }), 4_000);
  setTimeout(() => queryClient.invalidateQueries({ queryKey }), 10_000);
  setTimeout(() => queryClient.invalidateQueries({ queryKey }), 30_000);
}

// Query keys
export const petRecordsKeys = {
  all: ['petRecords'] as const,
  physicalMetrics: (petId: string) =>
    [...petRecordsKeys.all, 'physicalMetrics', petId] as const,
  vaccinations: (petId: string) =>
    [...petRecordsKeys.all, 'vaccinations', petId] as const,
  grooming: (petId: string) =>
    [...petRecordsKeys.all, 'grooming', petId] as const,
  medicalUpdates: (petId: string) =>
    [...petRecordsKeys.all, 'medicalUpdates', petId] as const,
};

// ============================================================================
// PHYSICAL METRICS
// ============================================================================

export function usePhysicalMetrics(petId: string) {
  return useQuery({
    queryKey: petRecordsKeys.physicalMetrics(petId),
    queryFn: async () => {
      const response = await getPhysicalMetrics(petId);
      return response.metrics;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreatePhysicalMetric(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePhysicalMetricRequest) =>
      createPhysicalMetric(petId, data),
    onSuccess: (response: { message: string; metric: PhysicalMetric }) => {
      queryClient.setQueryData<PhysicalMetric[]>(
        petRecordsKeys.physicalMetrics(petId),
        (old) => (old ? [response.metric, ...old] : [response.metric])
      );
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.physicalMetrics(petId));
    },
  });
}

// ============================================================================
// VACCINATIONS
// ============================================================================

export function useVaccinations(petId: string) {
  return useQuery({
    queryKey: petRecordsKeys.vaccinations(petId),
    queryFn: async () => {
      try {
        const response = await getVaccinations(petId);
        return response.vaccinations ?? [];
      } catch (err: unknown) {
        // Keep the Health Records screen usable even if list fetch fails.
        // Users can still add a new record, and the cache updates on success.
        console.warn('Vaccinations fetch failed, showing empty list:', err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateVaccination(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVaccinationRequest) =>
      createVaccination(petId, data),
    onSuccess: (response: { message: string; vaccination: Vaccination }) => {
      queryClient.setQueryData<Vaccination[]>(
        petRecordsKeys.vaccinations(petId),
        (old) => (old ? [response.vaccination, ...old] : [response.vaccination])
      );
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.vaccinations(petId));
    },
  });
}

export function useUpdateVaccination(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      vaccinationId,
      data,
    }: {
      vaccinationId: string;
      data: UpdateVaccinationRequest;
    }) => updateVaccination(petId, vaccinationId, data),
    onSuccess: (
      response: { message: string; vaccination: Vaccination },
      variables
    ) => {
      const updatedVaccination =
        response.vaccination && response.vaccination.id
          ? response.vaccination
          : null;
      const docHash =
        variables.data.documentHash !== undefined && variables.data.documentHash
          ? variables.data.documentHash
          : updatedVaccination?.documentHash;
      queryClient.setQueryData<Vaccination[]>(
        petRecordsKeys.vaccinations(petId),
        (old) =>
          old
            ? old.map((v) =>
                v.id === variables.vaccinationId
                  ? {
                      ...v,
                      ...(updatedVaccination ?? {}),
                      documentHash: docHash ?? v.documentHash,
                    }
                  : v
              )
            : updatedVaccination
              ? [updatedVaccination]
              : []
      );
      void queryClient.invalidateQueries({
        queryKey: petRecordsKeys.vaccinations(petId),
      });
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.vaccinations(petId));
    },
  });
}

// ============================================================================
// GROOMING RECORDS
// ============================================================================

export function useGroomingRecords(petId: string) {
  return useQuery({
    queryKey: petRecordsKeys.grooming(petId),
    queryFn: async () => {
      try {
        const response = await getGroomingRecords(petId);
        return response.groomingRecords ?? [];
      } catch (err: unknown) {
        // Treat any fetch error (404, 500, network) as empty list so the UI
        // still shows "Grooming Records" + "Add Record" and users can add records.
        console.warn('Grooming records fetch failed, showing empty list:', err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateGroomingRecord(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGroomingRecordRequest) =>
      createGroomingRecord(petId, data),
    onSuccess: (response: {
      message: string;
      groomingRecord: GroomingRecord;
    }) => {
      queryClient.setQueryData<GroomingRecord[]>(
        petRecordsKeys.grooming(petId),
        (old) =>
          old ? [response.groomingRecord, ...old] : [response.groomingRecord]
      );
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.grooming(petId));
    },
  });
}

export function useUpdateGroomingRecord(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groomingId,
      data,
    }: {
      groomingId: string;
      data: UpdateGroomingRecordRequest;
    }) => updateGroomingRecord(petId, groomingId, data),
    onSuccess: (
      response: {
        message: string;
        groomingRecord: GroomingRecord;
      },
      variables
    ) => {
      const updatedRecord =
        response.groomingRecord && response.groomingRecord.id
          ? response.groomingRecord
          : null;
      const docHash =
        variables.data.documentHash !== undefined && variables.data.documentHash
          ? variables.data.documentHash
          : updatedRecord?.documentHash;
      queryClient.setQueryData<GroomingRecord[]>(
        petRecordsKeys.grooming(petId),
        (old) =>
          old
            ? old.map((r) =>
                r.id === variables.groomingId
                  ? {
                      ...r,
                      ...(updatedRecord ?? {}),
                      documentHash: docHash ?? r.documentHash,
                    }
                  : r
              )
            : updatedRecord
              ? [updatedRecord]
              : []
      );
      void queryClient.invalidateQueries({
        queryKey: petRecordsKeys.grooming(petId),
      });
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.grooming(petId));
    },
  });
}

// ============================================================================
// MEDICAL UPDATES
// ============================================================================

export function useMedicalUpdates(petId: string) {
  return useQuery({
    queryKey: petRecordsKeys.medicalUpdates(petId),
    queryFn: async () => {
      try {
        const response = await getMedicalUpdates(petId);
        return response.medicalUpdates ?? [];
      } catch (err: unknown) {
        // Keep the Health Records screen usable even if list fetch fails.
        // Users can still add a new record, and the cache updates on success.
        console.warn('Medical updates fetch failed, showing empty list:', err);
        return [];
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateMedicalUpdate(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMedicalUpdateRequest) =>
      createMedicalUpdate(petId, data),
    onSuccess: (response: {
      message: string;
      medicalUpdate: MedicalUpdate;
    }) => {
      queryClient.setQueryData<MedicalUpdate[]>(
        petRecordsKeys.medicalUpdates(petId),
        (old) =>
          old ? [response.medicalUpdate, ...old] : [response.medicalUpdate]
      );
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.medicalUpdates(petId));
    },
  });
}

export function useUpdateMedicalUpdate(petId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      medicalUpdateId,
      data,
    }: {
      medicalUpdateId: string;
      data: UpdateMedicalUpdateRequest;
    }) => updateMedicalUpdate(petId, medicalUpdateId, data),
    onSuccess: (
      response: {
        message: string;
        medicalUpdate: MedicalUpdate;
      },
      variables
    ) => {
      const updatedUpdate =
        response.medicalUpdate && response.medicalUpdate.id
          ? response.medicalUpdate
          : null;
      const docHash =
        variables.data.documentHash !== undefined && variables.data.documentHash
          ? variables.data.documentHash
          : updatedUpdate?.documentHash;
      queryClient.setQueryData<MedicalUpdate[]>(
        petRecordsKeys.medicalUpdates(petId),
        (old) =>
          old
            ? old.map((u) =>
                u.id === variables.medicalUpdateId
                  ? {
                      ...u,
                      ...(updatedUpdate ?? {}),
                      documentHash: docHash ?? u.documentHash,
                    }
                  : u
              )
            : updatedUpdate
              ? [updatedUpdate]
              : []
      );
      void queryClient.invalidateQueries({
        queryKey: petRecordsKeys.medicalUpdates(petId),
      });
      scheduleBlockchainRefetch(queryClient, petRecordsKeys.medicalUpdates(petId));
    },
  });
}
